import React, { useEffect, useMemo, useState } from 'react';
import { Label } from '../../types/label';
import { scaledPositionToViewport } from '../../lib/coordinates';
import { PDFViewer } from 'pdfjs-dist/web/pdf_viewer';
import { NumberSize, Resizable } from 're-resizable';
import { getLabelStatus } from '../../lib/util/status';
import { Position } from '../../types/result';
import { Direction } from 're-resizable/lib/resizer';
import { getPageFromElement } from '../../lib/pdfjs-dom';

type Props = {
  viewer: PDFViewer;
  highlightedWord: Label;
  selectedWord: Label | null;
  pageNumber: number;
  onEditThisLabel: (label: Label, position: Position, page: { number: number }) => any;
  allowResize: boolean;
  publicDemo?: boolean;
};

export const HighlightedWord = ({
  viewer,
  highlightedWord,
  selectedWord,
  pageNumber,
  onEditThisLabel,
  allowResize,
  publicDemo,
}: Props) => {
  // Keep position of the label in a local state object
  // -> base the css styling on these values
  // -> the resize event can update this and immediately reflect changes
  // => underlying reason: the out-of-the-box resizing does not work in "top" or "left" directions,
  //    since the label is positioned absolutely. Updating the position on every resize fixes this.

  const [position, setPosition] = useState<any>(highlightedWord.location);

  // Keep track of the resize delta, and update on every resize so we can compare current and previous
  const [deltaX, setDeltaX] = useState<number>(0);
  const [deltaY, setDeltaY] = useState<number>(0);

  useEffect(() => {
    if (highlightedWord.location) {
      setPosition(highlightedWord.location);
    }
  }, [highlightedWord]);

  // Keep track of the scaledPosition, which gives us the left, right and width, height properties for css

  const scaledPosition = useMemo(() => {
    if (!highlightedWord.location || highlightedWord.page === null) return null;
    return scaledPositionToViewport(viewer, position, highlightedWord.page);
  }, [position, highlightedWord, viewer]);

  // RESIZING:

  // RESIZE-STOP, only fires on the end of the resizing, and edits the label with the new position
  // -> position state is updated on every resize-event, so when the resizing stops, the current
  //    position state already contains exactly the values we need on this label

  const onResizeStop = () => {
    onEditThisLabel(highlightedWord, position, { number: pageNumber });

    setDeltaX(0);
    setDeltaY(0);
  };

  // RESIZE HELPERS

  const movePointHorizontally = (
    point: number,
    horizontalDirection: 'left' | 'right' | null,
    singleWidthUnit: number
  ) => {
    if (horizontalDirection === 'right') {
      point = point + singleWidthUnit;
    } else if (horizontalDirection === 'left') {
      point = point - singleWidthUnit;
    }
    return point;
  };

  const movePointVertically = (point: number, verticalDirection: 'top' | 'bottom' | null, singleHeightUnit: number) => {
    if (verticalDirection === 'bottom') {
      point = point + singleHeightUnit;
    } else if (verticalDirection === 'top') {
      point = point - singleHeightUnit;
    }
    return point;
  };

  // MAIN RESIZE FUNCTION, fires on every resize event and offsets necessary points with one unit of the viewport-width
  // This updates the local state "position", which is used by the css to display the bounding box
  // -> this way resizing in every direction works and results in a change on the screen
  // -> only in the onResizeStop-function, we will persist the new position values on the edited label

  const onResize = (e: TouchEvent | MouseEvent, handleDirection: Direction, ref: HTMLElement, delta: NumberSize) => {
    const page = getPageFromElement(e.target as HTMLElement);
    if (!page) {
      return;
    }

    // First determine direction of the resizing, separately for horizontal and vertical
    // The direction prop being passed into this function is only referencing the handle being dragged,
    // but this handle (f.e. "left") can still be dragged in both directions (left and right)

    const horizontalDirection = ['left', 'bottomLeft', 'topLeft'].includes(handleDirection)
      ? delta.width > deltaX
        ? 'left'
        : delta.width < deltaX
        ? 'right'
        : null
      : ['right', 'bottomRight', 'topRight'].includes(handleDirection)
      ? delta.width < deltaX
        ? 'left'
        : delta.width > deltaX
        ? 'right'
        : null
      : null;

    const verticalDirection = ['bottom', 'bottomLeft', 'bottomRight'].includes(handleDirection)
      ? delta.height > deltaY
        ? 'bottom'
        : delta.height < deltaY
        ? 'top'
        : null
      : ['top', 'topLeft', 'topRight'].includes(handleDirection)
      ? delta.height < deltaY
        ? 'bottom'
        : delta.height > deltaY
        ? 'top'
        : null
      : null;

    // Update state values for deltaX and deltaY with the passed values from the resizeFunction
    // -> this is used in the comparison above to calculate in which direction we're scrolling

    setDeltaX(delta.width);
    setDeltaY(delta.height);

    // Calculate how much a single "resize" is in "pageView units" -> same Calculation as in the "getScaled" function

    const pageView = viewer.getPageView(page.number - 1);
    const viewport = pageView.viewport;

    const { width, height } = viewport;

    const singleWidthUnit = 1 / width;
    const singleHeightUnit = 1 / height;

    const widthOffset = Math.abs(delta.width - deltaX) * singleWidthUnit;
    const heightOffset = Math.abs(delta.height - deltaY) * singleHeightUnit;

    // Check every handleDirection option and move the necessary coordinates into this direction

    let newPosition = { ...position };

    switch (handleDirection) {
      case 'right':
        newPosition.x2 = movePointHorizontally(newPosition.x2, horizontalDirection, widthOffset);
        break;
      case 'left':
        newPosition.x1 = movePointHorizontally(newPosition.x1, horizontalDirection, widthOffset);
        break;
      case 'top':
        newPosition.y1 = movePointVertically(newPosition.y1, verticalDirection, heightOffset);
        break;
      case 'bottom':
        newPosition.y2 = movePointVertically(newPosition.y2, verticalDirection, heightOffset);
        break;
      case 'topLeft':
        newPosition.x1 = movePointHorizontally(newPosition.x1, horizontalDirection, widthOffset);
        newPosition.y1 = movePointVertically(newPosition.y1, verticalDirection, heightOffset);
        break;
      case 'bottomLeft':
        newPosition.x1 = movePointHorizontally(newPosition.x1, horizontalDirection, widthOffset);
        newPosition.y2 = movePointVertically(newPosition.y2, verticalDirection, heightOffset);
        break;
      case 'topRight':
        newPosition.x2 = movePointHorizontally(newPosition.x2, horizontalDirection, widthOffset);
        newPosition.y1 = movePointVertically(newPosition.y1, verticalDirection, heightOffset);
        break;
      case 'bottomRight':
        newPosition.x2 = movePointHorizontally(newPosition.x2, horizontalDirection, widthOffset);
        newPosition.y2 = movePointVertically(newPosition.y2, verticalDirection, heightOffset);
        break;
    }

    setPosition(newPosition);
  };

  return (
    <div key={highlightedWord.id} id={highlightedWord.id}>
      <Resizable
        size={{
          width: scaledPosition?.position.width,
          height: scaledPosition?.position.height,
        }}
        enable={{
          top: allowResize,
          right: allowResize,
          bottom: allowResize,
          left: allowResize,
          topRight: allowResize,
          bottomRight: allowResize,
          bottomLeft: allowResize,
          topLeft: allowResize,
        }}
        onResize={onResize}
        onResizeStop={onResizeStop}
        className={selectedWord && highlightedWord.id === selectedWord.id ? 'connectLineA' : ''}
        style={{
          position: 'absolute',
          opacity: 0.4,
          backgroundColor: publicDemo
            ? '#00b9ff'
            : highlightedWord.color
            ? highlightedWord.color
            : getLabelStatus(highlightedWord).color,
          border: selectedWord && highlightedWord.id === selectedWord.id ? '2px solid #ff4d4f' : '0px',
          ...scaledPosition?.position,
        }}
      />
    </div>
  );
};
